import { useEffect, useState } from 'react';

import { BadgeCheck, CheckCircle, ChevronRight, Clock, CreditCard, Shield } from 'lucide-react';

export default function ViewInscriptions() {
  const [state, setState] = useState<'idle' | 'opening' | 'live' | 'paid'>('idle');

  useEffect(() => {
    const t1 = setTimeout(() => setState('opening'), 700);
    const t2 = setTimeout(() => setState('live'), 1700);
    const t3 = setTimeout(() => setState('paid'), 3100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const live = state === 'live' || state === 'paid';

  return (
    <div className='grid md:grid-cols-2 gap-8 items-start demo-view-enter'>
      {/* Left */}
      <div className='space-y-5'>
        <div className='flex items-center gap-2'>
          {(['Ouverture', 'Paiements', 'Validation'] as const).map((s, i) => (
            <div key={s} className='flex items-center gap-2'>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                  i === 0
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'
                    : 'bg-slate-100 border border-slate-200 text-slate-400 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                }`}
              >
                {i === 0 ? '1' : <span className='text-[10px]'>{i + 1}</span>}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block ${i === 0 ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-600'}`}
              >
                {s}
              </span>
              {i < 2 && <ChevronRight className='w-3 h-3 text-slate-300 dark:text-slate-700' />}
            </div>
          ))}
        </div>

        <h3 className='text-xl font-black text-slate-900 dark:text-white'>Inscriptions & paiements</h3>

        <div className='bg-slate-50 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 rounded-2xl p-5'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4'>Statut</p>

          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <span className='text-sm text-slate-500 dark:text-slate-400'>Inscriptions</span>
              <span
                className={`text-sm font-black ${live ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}
              >
                {state === 'opening' ? 'Ouverture…' : live ? 'Ouvert' : 'Fermé'}
              </span>
            </div>

            <div className='flex items-center justify-between'>
              <span className='text-sm text-slate-500 dark:text-slate-400'>Paiement Stripe</span>
              <span
                className={`text-sm font-black ${state === 'paid' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`}
              >
                {state === 'paid' ? 'OK' : 'En attente'}
              </span>
            </div>

            <div className='flex items-center justify-between'>
              <span className='text-sm text-slate-500 dark:text-slate-400'>Places restantes</span>
              <span className='text-sm font-black text-orange-500 dark:text-orange-400'>14 / 128</span>
            </div>
          </div>

          <div className='mt-5 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden'>
            <div className='h-full bg-linear-to-r from-orange-500 to-red-500 rounded-full w-[90%]' />
          </div>

          <p className='mt-3 text-[10px] text-slate-500 dark:text-slate-600'>
            Listes auto : confirmés, en attente, liste d’attente. Emails transactionnels inclus.
          </p>
        </div>
      </div>

      {/* Right — CTA */}
      <div className='space-y-4'>
        <div className='bg-white border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700 rounded-2xl p-5'>
          <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4'>Dernière inscription</p>

          <div className='flex items-start gap-3'>
            <div className='w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-black text-white text-sm shrink-0'>
              PD
            </div>
            <div className='flex-1 min-w-0'>
              <p className='font-bold text-slate-900 dark:text-white text-sm truncate'>Dubois / Bernard</p>
              <p className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>Doublette · 30 €</p>
            </div>

            {state === 'paid' ? (
              <BadgeCheck className='w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0' />
            ) : (
              <span className='w-5 h-5 border-2 border-blue-500/60 border-t-transparent rounded-full animate-spin inline-block shrink-0' />
            )}
          </div>

          <div className='mt-4 flex items-center justify-between'>
            <span className='text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5'>
              <Clock className='w-3.5 h-3.5' /> il y a 2 min
            </span>
            <span className='text-xs font-black text-blue-600 dark:text-blue-400'>Voir le reçu</span>
          </div>
        </div>

        <button
          className={`w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 transition-all duration-500 ${
            live
              ? 'bg-linear-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-blue-600/30 dark:hover:shadow-blue-900/40 hover:-translate-y-0.5'
              : 'bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-600'
          }`}
        >
          <CreditCard className='w-5 h-5' />
          {live ? 'Ouvrir les inscriptions' : 'Préparation…'}
        </button>

        <div className='flex items-center justify-center gap-5 text-xs text-slate-500 dark:text-slate-600'>
          <span className='flex items-center gap-1.5'>
            <Shield className='w-3 h-3' />
            Stripe sécurisé
          </span>
          <span className='flex items-center gap-1.5'>
            <CheckCircle className='w-3 h-3' />
            Emails auto
          </span>
        </div>

        <p className='text-[10px] text-slate-500 dark:text-slate-700 text-center leading-relaxed'>
          Les infos du concours sont sous votre responsabilité (organisateur). Les paiements sont tracés.
        </p>
      </div>
    </div>
  );
}
